import React, { useState, useEffect } from "react";
import axios from "axios";
import MaterialGroupSelect from "../MaterialGroupSelect";
import MaterialLocationSelect from "../MaterialLocationSelect";
import { toast } from "react-toastify";
import LoadingOverlay from "../LoadingOverlay";
import ConfirmDialog from "../ConfirmDialog";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { formatNumber } from "../../utils/numbers";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400";

const LIST_UNIT = ["ROLL", "SHEET", "PLY", "KG", "PCS", "RIM"];

const fmtDate = (d) => (d ? format(new Date(d), "dd MMM yyyy") : "-");
const usageTh = "px-2 py-1.5 text-left font-semibold";
const usageTd = "px-2 py-1.5 whitespace-nowrap";

function MaterialListModal({ dataSet, onClose, refreshData }) {
  const [selectedMatGroup, setSelectedMatGroup] = useState(null);
  const [productName, setProductName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [productUnit, setProductUnit] = useState(LIST_UNIT[0]);
  const [productLocation, setProductLocation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [usage, setUsage] = useState(null);
  const [confirmCode, setConfirmCode] = useState("");
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.user_lvl === 1;

  useEffect(() => {
    if (dataSet) {
      setProductName(dataSet.product_name || "");
      setProductCode(dataSet.product_code || "");
      setProductUnit(dataSet.unit || LIST_UNIT[0]);
    }
  }, [dataSet]);

  const updateOrder = async () => {
    setIsLoading(true);
    try {
      const res = await axios.patch(
        "https://eps.elkomprintama.com/patch/api/store/store/mats",
        {
          productCode: productCode.toUpperCase(),
          productName: productName.toUpperCase(),
          groupId: selectedMatGroup,
          unit: productUnit,
          productLocationId: productLocation,
          productId: dataSet.product_id,
        },
      );
      if (res.status === 200) {
        toast.success(res.data.msg);
        onClose();
        refreshData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Update failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const openDelete = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/mats/${dataSet.product_id}/usage`,
      );
      setConfirmCode("");
      setUsage(res.data.result);
    } catch (err) {
      toast.error(
        err?.response?.data?.msg || "Failed to check material usage.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMaterial = async () => {
    setIsLoading(true);
    try {
      const res = await axios.delete(
        `https://eps.elkomprintama.com/delete/api/store/mats/${dataSet.product_id}`,
        { data: { confirmCode } },
      );
      toast.success(res.data.msg);
      setUsage(null);
      onClose();
      refreshData();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Delete failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!productName || !productCode || !selectedMatGroup) {
      toast.warning("Please fill in all required fields.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg relative overflow-hidden">
      {isLoading && <LoadingOverlay message="Updating…" />}

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-sm font-bold text-gray-800">Edit Material</h2>
          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
            {dataSet?.product_name}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <MaterialLocationSelect
          dataSet={dataSet}
          onChangeList={setProductLocation}
        />

        {/* Name + Code */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-4">
            <label className={lbl}>Product Name</label>
            <input
              className={inp}
              name="productname"
              type="text"
              placeholder="Product Name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
          </div>
          <div>
            <label className={lbl}>Code</label>
            <input
              className={inp}
              name="productcode"
              type="text"
              placeholder="Code"
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
            />
          </div>
        </div>

        <MaterialGroupSelect
          dataSet={dataSet}
          onChangeList={setSelectedMatGroup}
        />

        <div>
          <label className={lbl}>Unit</label>
          <select
            className={inp}
            value={productUnit}
            onChange={(e) => setProductUnit(e.target.value)}
          >
            {LIST_UNIT.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
          >
            Update
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
          >
            Close
          </button>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={openDelete}
            className="w-full py-2.5 rounded-xl text-sm font-bold text-red-600 bg-red-50 hover:bg-red-100 transition"
          >
            Delete Material
          </button>
        )}
      </form>

      {usage && (
        <div className="absolute inset-0 bg-white flex flex-col px-6 py-5 space-y-4 z-10">
          <div>
            <h2 className="text-sm font-bold text-red-600">Delete Material</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {usage.product.product_code} · {usage.product.product_name}
            </p>
          </div>

          <div className="flex-1 min-h-0 overflow-auto space-y-4 text-xs text-gray-700">
            <p>
              Current stock:{" "}
              <b>
                {formatNumber(usage.currentStock)} {usage.product.unit}
              </b>
            </p>

            <div>
              <p className={lbl}>Transactions ({usage.totalTransactions})</p>
              {usage.transactionSummary.length === 0 ? (
                <p className="text-gray-400 italic">No transactions</p>
              ) : (
                <>
                  <table className="w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-100 text-gray-500">
                      <tr>
                        <th className={usageTh}>Type</th>
                        <th className={usageTh}>Count</th>
                        <th className={usageTh}>Volume</th>
                        <th className={usageTh}>Period</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.transactionSummary.map((t) => (
                        <tr key={t.type} className="border-t">
                          <td className={usageTd}>{t.type}</td>
                          <td className={usageTd}>{t.total}</td>
                          <td className={usageTd}>
                            {t.totalIn > 0 && `+${formatNumber(t.totalIn)}`}
                            {t.totalIn > 0 && t.totalOut > 0 && " / "}
                            {t.totalOut > 0 && `-${formatNumber(t.totalOut)}`}
                            {t.totalIn === 0 && t.totalOut === 0 && "0"}
                          </td>
                          <td className={usageTd}>
                            {fmtDate(t.firstTime)} – {fmtDate(t.lastTime)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <p className="mt-2 mb-1 text-gray-500">Latest transactions</p>
                  <ul className="space-y-0.5">
                    {usage.recentTransactions.map((t) => (
                      <li key={t.transaction_id}>
                        {fmtDate(t.transaction_time)} · {t.transaction_type} ·{" "}
                        {t.stock_in > 0
                          ? `+${formatNumber(t.stock_in)}`
                          : `-${formatNumber(t.stock_out)}`}
                        {t.transaction_for && ` · ${t.transaction_for}`}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div>
              <p className={lbl}>Linked jobsheets ({usage.linkedJobsheets})</p>
              {usage.jobsheets.length === 0 ? (
                <p className="text-gray-400 italic">No linked jobsheets</p>
              ) : (
                <ul className="space-y-0.5">
                  {usage.jobsheets.map((j) => (
                    <li key={j.job_id}>
                      {fmtDate(j.job_date)} · <b>{j.job_name}</b>
                      {j.cust_name && ` · ${j.cust_name}`}
                      {j.pn_cust && ` · ${j.pn_cust}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {usage.pendingTransactions > 0 ? (
            <p className="text-xs text-red-600">
              This material still has {usage.pendingTransactions} pending
              transaction(s). Approve or cancel them before deleting.
            </p>
          ) : (
            <>
              <p className="text-xs text-red-600">
                This permanently deletes the material
                {usage.totalTransactions > 0 &&
                  ` and all ${usage.totalTransactions} transaction record(s)`}
                {usage.linkedJobsheets > 0 &&
                  `, and unlinks it from ${usage.linkedJobsheets} jobsheet(s)`}
                . This cannot be undone.
              </p>
              <div>
                <label className={lbl}>
                  Type <b>{usage.product.product_code}</b> to confirm
                </label>
                <input
                  className={inp}
                  type="text"
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="flex gap-2 pt-1 mt-auto">
            {usage.pendingTransactions === 0 && (
              <button
                type="button"
                disabled={confirmCode !== usage.product.product_code}
                onClick={deleteMaterial}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Delete Permanently
              </button>
            )}
            <button
              type="button"
              onClick={() => setUsage(null)}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Update this material?"
          onConfirm={() => {
            setShowConfirm(false);
            updateOrder();
            setUploadPending(false);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}
    </div>
  );
}

export default MaterialListModal;
